import React, { useState, useEffect } from "react";
import { useTranslation } from "react-i18next";
import { useForm } from "react-hook-form";
import { useNavigate, useParams } from "react-router-dom";
import { makeStyles } from "@material-ui/core/styles";
import { Paper, InputLabel, Grid } from "@material-ui/core";
import CredentialService from "../../../../services/CredentialService/CredentialService";
import CredentialDefinition from "./Sections/CredentialDefinition";
import { checkEquality } from "../../../../helpers/helper";
import Info from "./Components/Info";
import Spinner from "../../../../components/Spinner/Spinner";
import { useSetRecoilState } from 'recoil';
import { alertState } from '../../../../recoil-store/recoil-state';
import "./Components/Checkbox.css";
import "./AddNewCredential.css";

// Element's style attributes
const useStyles = makeStyles((theme) => ({
  title: {
    fontWeight: 800,
    fontSize: 16,
    color: "#46474E",
    marginBottom: 20,
  },
  inputTitle: {
    fontWeight: 700,
    fontSize: 11,
    color: "#8C90A2",
    marginBottom: 5,
  },
  input: {
    border: "0.5px solid #D5D5D5",
    boxSizing: "border-box",
    borderRadius: "2px",
    height: 35,
    width: 280,
  },
  bottomSection: {
    display: "flex",
    justifyContent: "space-between",
    alignItems: "center",
    width: "100%",
  },
  submitButtons: {
    display: "flex",
    justifyContent: "space-between",
  },
  button: {
    borderRadius: 4,
    width: 100,
    height: 35,
    fontWeight: 700,
    cursor: "pointer",
    boxShadow: "0px 0px 8px -3px rgba(0, 0, 0, 0.25)",
  },
  cancel: {
    color: "#162436",
    backgroundColor: "#fff",
    border: "1px solid #000000",
    marginRight: 30,
  },
  submit: {
    color: "#fff",
    backgroundColor: "#162436",
    border: "none",
  },
  toggleTitle: {
    fontWeight: 700,
    fontSize: 11,
    color: "#8C90A2",
  },
  toggleButtons: {
    display: "flex",
    flexDirection: "row",
    justifyContent: "space-between",
    alignItems: "center",
    width: 150,
    marginTop: 10,
  },
}));

function CredentialForm(props) {
  const history = useNavigate();
  let match = useParams();
  let form = useForm();
  const { register, handleSubmit, errors, getValues, setValue } = form;
  const { t } = useTranslation();
  const classes = useStyles();
  const id = match.id;
  const [isLoading, setIsLoading] = useState(true);
  const [deletedAttr, setDeletedAttr] = useState([]);
  const [variables, setVariables] = useState({
    name: "",
    description: "",
    is_private: false,
    attributes: [
      {
        id: "",
        name: "",
        description: "",
        masked_flag: false,
        user_provided_flag: false,
        value: "",
      },
    ],
  });
  const setAlert = useSetRecoilState(alertState)

  useEffect(() => {
    if (id) {
      fetchData(id)
        .then((data) => {
          if (data.message === "ACCESS-DENIED") {
            history(-1);
            setAlert(alert => ({ ...alert, show: true, message: t("MESSAGES.CREDENTIAL.CREDENTIAL-ACCESS-DENIED"), severity: 'error' }));
            return;
          }
          let dataAttr = data.data.connection_attributes.map((item) => {
            return {
              id: item.id,
              name: item.name,
              description: item.description,
              masked_flag: item.masked_flag,
              user_provided_flag: item.user_provided_flag,
              value: item.attribute_values.length
                ? item.attribute_values[0].value
                : "",
              attrValueCreatedBy: item.attribute_values.length
                ? item.attribute_values[0].created_by
                : data.data.owner_id,
            };
          });
          setVariables({
            ...variables,
            id: data.data.id,
            name: data.data.name,
            description: data.data.description,
            is_private: data.data.is_private,
            resource_id: data.data.resource_id,
            owner_id: data.data.owner_id,
            attributes: [...dataAttr],
          });
          setIsLoading(false);
          setValue("name", data.data.name);
          setValue("description", data.data.description);
          setValue("private", data.data.is_private);
        })
        .catch((err) => console.log(err));
    } else {
      setIsLoading(false);
    }
  }, []);

  const fetchData = async (id) => {
    try {
      const response = await CredentialService.getCredentialByFilter(id);
      return await response;
    } catch (error) {
      return error.response ? error.response.data : undefined;
    }
  };

  const addVariableSection = (e) => {
    const data = getValues();
    let x = data.attributes.map((item, index) => {
      return {
        id: variables.attributes[index]?.id,
        name: item.name,
        description: item.description,
        value: item.value,
        masked_flag: item.masked_flag,
        user_provided_flag: item.user_provided_flag,
      };
    });
    setVariables({
      ...variables,
      attributes: [
        ...x,
        {
          id: "",
          name: "",
          description: "",
          value: "",
          masked_flag: false,
          user_provided_flag: false,
        },
      ],
    });
  };

  const removeVariableSection = (e, index) => {
    if (id) {
      if (variables.attributes[e.target.id].id) {
        setDeletedAttr([...deletedAttr, variables.attributes[e.target.id].id]);
      }
      // const restArray = variables.attributes.filter((item) => item.id !== variables.attributes[e.target.id].id && item)
      for (let i = 0; i < variables.attributes.length; i++) {
        variables.attributes[i].name = getValues(`attributes[${i}].name`);
        variables.attributes[i].description = getValues(
          `attributes[${i}].description`
        );
        variables.attributes[i].value = getValues(`attributes[${i}].value`);
        variables.attributes[i].masked_flag = getValues(
          `attributes[${i}].masked_flag`
        );
        variables.attributes[i].user_provided_flag = getValues(
          `attributes[${i}].user_provided_flag`
        );
      }
      let attr = JSON.parse(JSON.stringify(variables.attributes));
      attr.splice(index, 1);
      setVariables({ ...variables, attributes: attr });
    } else {
      const data = getValues();
      const tempAttributes = [...data.attributes.slice(0, e.target.id)];
      const newAttributes = [
        ...tempAttributes,
        ...data.attributes.slice(parseInt(e.target.id) + 1),
      ];
      setVariables({ ...variables, attributes: newAttributes });
    }
  };

  const onSubmit = async (formData, e) => {
    let newFormData = { ...formData, resource_id: "" };
    if (id) {
      //if fields will be update, the variable of attribute will have an array which will be contain a value with what will be update!
      let whichCount = variables.attributes.length; //=> it determines length of the for loop based on whether or not there is data to update
      let willBeUpdatedArr;
      let willBeNewAddedAttr;
      let tempLength = newFormData.attributes.length;
      let willBeUpdateAttrVal = [];
      let willBeNewAttrVal = [];
      for (let i = 0; i < tempLength; i++) {
        if (!variables.attributes[i].id) {
          if (!willBeNewAddedAttr) {
            willBeNewAddedAttr = [newFormData.attributes[i]];
          } else {
            willBeNewAddedAttr = [
              ...willBeNewAddedAttr,
              newFormData.attributes[i],
            ];
          }
          if (newFormData.attributes[i].value) {
            if (variables.attributes[i].attrValueId) {
              // update

              willBeUpdateAttrVal.push({
                connection_attribute_id: variables.attributes[i].id,
                created_by: variables.attributes[i].attrValueCreatedBy,
                value: newFormData.attributes[i].value,
              });
            } else {
              // add

              willBeNewAttrVal.push({
                connection_attribute_id: variables.attributes[i].id,
                value: newFormData.attributes[i].value,
              });
            }
          }
        } else {
          if (!willBeUpdatedArr) {
            willBeUpdatedArr = [newFormData.attributes[i]];
          } else {
            willBeUpdatedArr = [...willBeUpdatedArr, newFormData.attributes[i]];
          }

          willBeUpdateAttrVal.push({
            connection_attribute_id: variables.attributes[i].id,
            created_by: variables.owner_id,
            value: newFormData.attributes[i].value
              ? newFormData.attributes[i].value
              : "",
          });
          // }

        }
        whichCount = willBeUpdatedArr.length;
      }
      let attributes = checkEquality(willBeUpdatedArr, variables, whichCount);

      if (willBeNewAddedAttr)
        attributes = [...attributes, ...willBeNewAddedAttr];

      let updatedData = {
        id: variables.id,
        name: formData.name,
        description: formData.description,
        is_private: formData.is_private,
        resource_id: variables.resource_id,
        owner_id: variables.owner_id,
        attributes: attributes,
      };
      if (deletedAttr.length > 0)
        updatedData = {
          ...updatedData,
          willBeDeletedAttributesIds: deletedAttr,
        };

      const response = await CredentialService.editCredential(updatedData);
      if (response.status) {

        const credentialAttr = response.data?.addedFields;
        let attr = [];
        for (let i = 0; i < credentialAttr?.length; i++) {
          for (let k = 0; k < newFormData?.attributes?.length; k++) {
            if (
              credentialAttr[i].name === newFormData.attributes[k].name &&
              credentialAttr[i].description === newFormData.attributes[k].description

            ) {
              if (!credentialAttr[i].user_provided_flag) {
                attr.push({
                  connection_attribute_id: credentialAttr[i].id,
                  value: newFormData.attributes[k].value,
                });
              }
              break;
            }
          }
        }
        const requestDate = {
          willBeUpdatedValues: willBeUpdateAttrVal,
          willBeAddedValues: attr,
        };

        const attrResponse = await CredentialService.updateCredentialValue(
          requestDate,
          id
        );
        if (attrResponse.status) {
          setAlert(alert => ({ ...alert, show: true, message: t(`MESSAGES.CREDENTIAL.${response.message}`), severity: 'success' }));
          history("/management/vault/custom-credentials");
        } else {
          setAlert(alert => ({ ...alert, show: true, message: t(`MESSAGES.CREDENTIAL.CREDENTIAL-UPDATE-FAILED`), severity: 'error' }));
        }
      } else {
        setAlert(alert => ({ ...alert, show: true, message: t(`MESSAGES.CREDENTIAL.CREDENTIAL-UPDATE-FAILED`), severity: 'error' }));
      }
    } else {
      const response = await CredentialService.newCredential(newFormData);
      if (response) {
        if (response.status) {
          const credentialAttr = response.data.connection_attributes;
          let attr = [];
          for (let i = 0; i < credentialAttr.length; i++) {
            for (let k = 0; k < newFormData.attributes.length; k++) {
              if (
                credentialAttr[i].name === newFormData.attributes[k].name &&
                credentialAttr[i].description ===
                newFormData.attributes[k].description
              ) {
                if (!credentialAttr[i].user_provided_flag) {
                  attr.push({
                    connection_attribute_id: credentialAttr[i].id,
                    created_by: variables.attributes[i].attrValueCreatedBy,
                    value: newFormData.attributes[k].value,
                  });
                }
                break;
              }
            }
          }
          if (attr.length) {
            const attrResponse = await CredentialService.newCredentialValue(
              attr,
              response.data.id
            );
            if (attrResponse) {
              if (attrResponse.status) {
                setAlert(alert => ({ ...alert, show: true, message: t(`MESSAGES.CREDENTIAL.${response.message}`), severity: response.status ? 'success' : 'error' }));
                history("/management/vault/custom-credentials");
              } else {
                setAlert(alert => ({ ...alert, show: true, message: t("MESSAGES.CREDENTIAL.CREDENTIAL-ADDED-FAILED"), severity: response.status ? 'success' : 'error' }));
              }
            } else {
              setAlert(alert => ({ ...alert, show: true, message: t('GENERAL.NETWORK-ERROR'), severity: 'error' }));
            }
          } else {
            setAlert(alert => ({ ...alert, show: true, message: t(`MESSAGES.CREDENTIAL.${response.message}`), severity: response.status ? 'success' : 'error' }));
            history("/management/vault/custom-credentials");
          }
        } else {
          setAlert(alert => ({ ...alert, show: true, message: t("MESSAGES.CREDENTIAL.CREDENTIAL-ADDED-FAILED"), severity: response.status ? 'success' : 'error' }));
        }
      } else {
        setAlert(alert => ({ ...alert, show: true, message: t('GENERAL.NETWORK-ERROR'), severity: 'error' }));
      }
    }
  };

  return (
    <div data-test="add-new-credential">
      {isLoading ? (
        <Spinner />
      ) : (
        <Paper style={{ height: "100%", padding: 30 }}>
          <div className={classes.title}>
            {t("CONTENT.ADD-NEW-CREDENTIAL.CREDENTIAL-INFORMATION")}
          </div>

          <form
            noValidate
            onSubmit={handleSubmit(onSubmit)}
            autoComplete="off"
            style={{
              display: "flex",
              flexDirection: "column",
              justifyContent: "space-evenly",
              fontSize: "14px",
            }}
            data-test="form"
          >
            <Grid>
              <Grid container>
                <Grid item md={4} xs={12}>
                  <InputLabel className={classes.inputTitle} htmlFor="name">
                    {t("CONTENT.ADD-NEW-CREDENTIAL.CREDENTIAL-NAME")} <span className="mandatory-field">*</span>
                  </InputLabel>
                  <input
                    className={classes.input}
                    name="name"
                    ref={register({ required: true })}
                  />
                  {errors.name?.type === "required" && (
                    <p className="required-visible">
                      {t("MESSAGES.AUTH-PAGES.ADD-NEW-CREDENTIAL.REQUIRED")}!
                    </p>
                  )}
                </Grid>
                <Grid item md={4} xs={12}>
                  <InputLabel
                    className={classes.inputTitle}
                    htmlFor="description"
                  >
                    {t("CONTENT.ADD-NEW-CREDENTIAL.CREDENTIAL-DESCRIPTION")}
                  </InputLabel>
                  <input
                    className={classes.input}
                    name="description"
                    ref={register({ required: false })}
                  />
                </Grid>
              </Grid>
              <Grid item md={3} xs={12} style={{display: "none"}}>
                <div className={classes.toggleButtons}>
                  <Info
                    value={t(
                      "CONTENT.ADD-NEW-CREDENTIAL.CREDENTIAL-IS-PRIVATE"
                    )}
                  />
                  <InputLabel
                    className={classes.toggleTitle}
                    htmlFor="is_private?"
                  >
                    {t("CONTENT.ADD-NEW-CREDENTIAL.CREDENTIAL-PRIVACY")}
                  </InputLabel>
                  <input
                    className="apple-switch"
                    type="checkbox"
                    name="is_private"
                    ref={register()}
                    onChange={(e) => { }}
                  />
                </div>
              </Grid>
            </Grid>
            <div className="hr-container">
              <hr />
            </div>
            <div className={classes.title}>
              {t("CONTENT.ADD-NEW-CREDENTIAL.VARIABLE-DEFINITION")}
            </div>

            <Grid container>
              {variables.attributes.map((item, i) => {
                return (
                  <CredentialDefinition
                    removeVariableSection={removeVariableSection}
                    setVariables={setVariables}
                    variables={variables}
                    getValues={getValues}
                    setValue={setValue}
                    register={register}
                    errors={errors}
                    key={i}
                    index={i}
                    addVariableSection={addVariableSection}
                    isLast={i === variables.attributes.length - 1}
                  />
                );
              })}
            </Grid>

            <div className={classes.bottomSection}>
              <div className={classes.submitButtons}>
                <button
                  type="reset"
                  onClick={() => {
                    history("/management/vault/custom-credentials");
                  }}
                  className={`${classes.button} ${classes.cancel}`}
                >
                  {t("CONTENT.ADD-NEW-CREDENTIAL.CANCEL")}
                </button>
                <button
                  type="submit"
                  className={`${classes.button} ${classes.submit}`}
                >
                  {t("CONTENT.ADD-NEW-CREDENTIAL.SAVE")}
                </button>
              </div>
            </div>
          </form>
        </Paper>
      )}
    </div>
  );
}

export default CredentialForm;
